import { z } from 'zod';

import {
  zCandidatePathway,
  zCandidateTier,
  zClientType,
  zDeclinedStatusFilter,
  zFunction,
  zIndustry,
  zIntroductionStatusFilter,
  zOperatingHighlights,
  zPipelineCandidateSortColumn,
  zRecommendationStatusFilter,
  zSortDirection,
} from 'client/zod.gen';
import { zBooleanFiltersArray, zFiltersArray } from 'helpers/router';

/**
 * Search params for the "add recommendations" wizard. Shared between the team
 * (`/teams/$teamId/...`) and user (`/users/$userId/...`) surfaces so both
 * routes validate identically.
 */
export const newRecommendationsSearchSchema = z.preprocess(
  (val) => {
    // If the value is undefined, null, or an empty object,
    // return undefined to go straight to the default value.
    if (!val) return undefined;
    if (typeof val === 'object' && val && Object.keys(val).length === 0) {
      return undefined;
    }
    return val;
  },
  z
    .object({
      // possible values are all the keys of PipelineCandidatesFilters
      filtersDialog: z
        .enum([
          'advisor_id',
          'connection_history',
          'operating_highlights',
          'function_expertise',
          'industry_expertise',
          'recsys_suggested',
          'warm',
          'tier',
          'type',
          'pathway',
          'underrepresented',
          'is_accepting_intros',
        ])
        .optional()
        .catch(undefined),
      advisor_id: zFiltersArray(),
      declined_status: zFiltersArray(zDeclinedStatusFilter),
      function_expertise: zFiltersArray(zFunction),
      industry_expertise: zFiltersArray(zIndustry),
      introduction_status: zFiltersArray(zIntroductionStatusFilter),
      is_accepting_intros: zBooleanFiltersArray(),
      operating_highlights: zFiltersArray(zOperatingHighlights),
      pathway: zFiltersArray(zCandidatePathway),
      order: zSortDirection.optional().catch('desc').default('desc'),
      recommendation_status: zFiltersArray(zRecommendationStatusFilter),
      recsys_suggested: zBooleanFiltersArray(),
      sort: zPipelineCandidateSortColumn.optional().catch(undefined),
      tier: zFiltersArray(zCandidateTier),
      type: zFiltersArray(zClientType),
      underrepresented: zBooleanFiltersArray(),
      warm: zBooleanFiltersArray(),
    })
    .optional()
    // If the search param object is undefined, zod will go straight here.
    .default({
      order: 'desc',
    })
    // If an uncaught error occurs during parsing, zod will go here.
    .catch({
      order: 'desc',
    }),
);

export type NewRecommendationsSearch = z.infer<
  typeof newRecommendationsSearchSchema
>;

export type FiltersDialogKey = NonNullable<
  NewRecommendationsSearch['filtersDialog']
>;
